import {
  safeGetItem,
  safeRemoveItem,
  safeSetItem,
} from '../helpers/safeStorage.js'
import { clickRequest, growthOnV2 } from './growth.js'
import {
  REFERRAL_COOKIE,
  isClickRef,
  parseReferralCookie,
} from './referral-redirects.js'

// Client half of the referral 301 (see ./referral-redirects.js for the why).
//
// The edge redirect can't touch localStorage, so it leaves the attribution in the `pc_ref`
// cookie. <ReferralHydrator> (root layout, outside the auth gate) runs hydrateReferral() on every
// page load, which:
//
//   1. Stores an Aiffiliate link's attribution as ONE group (aiffiliateUid, aiffiliateTracker,
//      landing, aiffiliateClickRef), and only when no aiffiliateUid is stored. The first
//      affiliate link locks the account, as the affiliate site promises ("no cookie window to
//      expire"). Until 7 October 2026 each key was copied on its own, first touch per key, so a
//      visitor who came through affiliate A's bare link and later B's tracked link was stored as
//      A's uid with B's tracker. Promptchan's own /r/ uid (one-f `affiliateUid`) and the
//      ad-network keys (/a/ links) still copy key by key, first touch per key.
//   2. Stores the group an email sign-in link carries (`aff`, `afft`, `affc`, `affl` on
//      /signup; SignUpComponent puts them there) the same way, only when none is stored, so an
//      email opened in another browser keeps the credit; then takes them off the address bar.
//   3. Reports the click: once per clickRef (the edge mints one per link per UTC day), to
//      POST {CLOUD}/api/record-event, or v2's POST /v2/affiliate-clicks for a visitor the
//      doorway's `growth` area puts on v2 (growth.js), for the link actually clicked (the
//      cookie), not the stored first one. Only Aiffiliate links have a clickRef. This replaced the header's
//      once-per-session ping (ClientHeaderWrapper), which re-counted the same visitor every
//      session for as long as localStorage held an affiliate: about ten clicks per sign-up.
//
// The cookie is NOT deleted here (it expires after 30 days, and a completed sign-up deletes it):
// reading it again changes nothing, and sign-up falls back to it when localStorage is empty or
// blocked (Safari's seven-day cap on script-written storage, private browsing). Nothing personal
// is stored or sent: a click carries the affiliate's ids, a random reference, its time, the
// referring site's host and the link's own sub-IDs.

// The group in each of its places: localStorage key → its pc_ref key and email-link param.
const GROUP = {
  aiffiliateUid: { cookie: 'aiffiliateUid', link: 'aff' },
  aiffiliateTracker: { cookie: 'aiffiliateTracker', link: 'afft' },
  landing: { cookie: 'landing', link: 'affl' },
  aiffiliateClickRef: { cookie: 'clickRef', link: 'affc' },
}
// The uid is written last, so a stored uid means the rest of its group was written too.
const WRITE_ORDER = [
  'aiffiliateTracker',
  'landing',
  'aiffiliateClickRef',
  'aiffiliateUid',
]

// Copied from the cookie key by key, first touch per key. The cookie is client-visible and
// therefore user-editable, so only these keys (and the group) are ever written.
const PER_KEY = [
  'affiliateUid', // /r/ links
  // advertiser / ad-network links (/a) — the keys useJuicyAdsCheck and useClickAduCheck used
  // to write client-side, and that SignUpComponent reads for the create_account payload.
  'advertiser',
  'dynamicS2S',
  'click_subid',
  'click_zoneid',
  'click_campaignid',
  'click_geo',
]

// What a click report carries, all from the cookie (the contract's record-event keys).
const REPORTED = [
  'aiffiliateUid',
  'aiffiliateTracker',
  'clickRef',
  'clickedAt',
  'landing',
  'referrerHost',
  'sub1',
  'sub2',
  'sub3',
  'sub4',
  'sub5',
]
// The clickRefs already reported from this browser. The cookie holds one click at a time, so a
// short list is plenty: it only has to outlast the cookie's current click.
const REPORTED_KEY = 'aiffiliateReportedClicks'
const REPORTED_MAX = 20

const usable = (value) =>
  typeof value === 'string' && value.length > 0 && value.length <= 2048
    ? value
    : null

// A group from wherever `read` reads (storage, the cookie, the link), or null without a uid.
function readGroup(read) {
  const uid = usable(read('aiffiliateUid'))
  if (!uid) return null
  const clickRef = read('aiffiliateClickRef')
  return {
    aiffiliateUid: uid,
    aiffiliateTracker: usable(read('aiffiliateTracker')),
    landing: usable(read('landing')),
    aiffiliateClickRef: isClickRef(clickRef) ? clickRef : null,
  }
}

const cookieGroup = (cookie) =>
  cookie ? readGroup((key) => cookie[GROUP[key].cookie]) : null

/**
 * The Aiffiliate group a sign-up reports: the stored one, else the one in the `pc_ref` cookie
 * (localStorage purged or blocked), else all null. `readItem` reads localStorage; `cookie` is
 * the parsed pc_ref (readReferralCookie).
 */
export function affiliateGroup(readItem, cookie) {
  return (
    readGroup(readItem) ||
    cookieGroup(cookie) || {
      aiffiliateUid: null,
      aiffiliateTracker: null,
      landing: null,
      aiffiliateClickRef: null,
    }
  )
}

/** Put a group on an email sign-in link's continue URL, for hydrateReferral to store there. */
export function carryAttribution(url, group) {
  for (const [key, { link }] of Object.entries(GROUP)) {
    if (group?.[key]) url.searchParams.set(link, group[key])
  }
  return url
}

function storeGroup(storage, group) {
  if (!group || storage.getItem('aiffiliateUid')) return
  // Every key of the group, a missing one removed, so nothing of an earlier click is left beside
  // this one (a tracker kept after its uid was cleared, say).
  for (const key of WRITE_ORDER) {
    if (group[key]) storage.setItem(key, group[key])
    else storage.removeItem(key)
  }
}

function storePerKey(storage, cookie) {
  for (const key of PER_KEY) {
    const value = usable(cookie[key])
    if (value && !storage.getItem(key)) storage.setItem(key, value)
  }
}

// Only on /signup, where the email link lands: elsewhere `aff` is too common a parameter name
// (an ad network's own, say) to let it lock an account to whatever it holds.
function storeFromLink({ url, storage, replaceUrl }) {
  if (url.pathname.replace(/\/+$/, '') !== '/signup') return
  const links = Object.values(GROUP).map(({ link }) => link)
  if (!links.some((link) => url.searchParams.has(link))) return
  storeGroup(
    storage,
    readGroup((key) => url.searchParams.get(GROUP[key].link)),
  )
  const clean = new URL(url)
  for (const link of links) clean.searchParams.delete(link)
  replaceUrl(clean.toString())
}

/** The record-event body for the cookie's click, or null when it holds none. */
export function clickReport(cookie) {
  if (!usable(cookie?.aiffiliateUid) || !isClickRef(cookie.clickRef))
    return null
  const body = {}
  for (const key of REPORTED) {
    const value = usable(cookie[key])
    if (value) body[key] = value
  }
  return body
}

function reportedClicks(storage) {
  try {
    const list = JSON.parse(storage.getItem(REPORTED_KEY))
    return Array.isArray(list) ? list : []
  } catch {
    return []
  }
}

// clickRefs sent, or being sent, from this page: a second call (React's development double
// effect, or storage that cannot remember) does not send the same click again.
const sentHere = new Set()

async function reportClick(cookie, { storage, send }) {
  const body = clickReport(cookie)
  if (!body) return false
  const ref = body.clickRef
  if (sentHere.has(ref) || reportedClicks(storage).includes(ref)) return false
  sentHere.add(ref)
  let sent = false
  try {
    sent = await send(body)
  } catch {
    // unsent: as below
  }
  if (!sent) {
    // Not marked, so the next page load tries again; Aiffiliate keeps click_ref unique, so a
    // report that did arrive after all is a duplicate there, never a second click.
    sentHere.delete(ref)
    return false
  }
  const kept = reportedClicks(storage).filter((known) => known !== ref)
  storage.setItem(
    REPORTED_KEY,
    JSON.stringify([...kept, ref].slice(-REPORTED_MAX)),
  )
  return true
}

function readCookie(name) {
  if (typeof document === 'undefined') return null
  return (
    document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`))
      ?.split('=')
      .slice(1)
      .join('=') || null
  )
}

/** The attribution in this browser's `pc_ref` cookie, or null. */
export const readReferralCookie = () =>
  parseReferralCookie(readCookie(REFERRAL_COOKIE))

async function sendClick(click) {
  // v1's as JSON, which its handler requires, so cross-origin it is preflighted, and a browser
  // that cannot preflight a keepalive request refuses it outright: the plain retry still
  // reports the click. v2's as text/plain, which needs no preflight.
  const { url, headers, body } = clickRequest(await growthOnV2(), click)
  const request = (keepalive) => ({
    method: 'POST',
    headers,
    body,
    keepalive,
    signal: AbortSignal.timeout(10000),
  })
  try {
    return (await fetch(url, request(true))).ok
  } catch {
    try {
      return (await fetch(url, request(false))).ok
    } catch {
      return false
    }
  }
}

function browserEnv() {
  return {
    cookie: readCookie(REFERRAL_COOKIE),
    url: new URL(window.location.href),
    storage: {
      getItem: safeGetItem,
      setItem: safeSetItem,
      removeItem: safeRemoveItem,
    },
    send: sendClick,
    // After this render's effects, once Next has patched history: given no state of its own, the
    // patched replaceState copies the router's and moves the router's URL along with the bar's.
    replaceUrl: (url) =>
      setTimeout(() => window.history.replaceState(null, '', url), 0),
  }
}

/**
 * Store the referral attribution and report its click (the comment at the top). `env` is the
 * browser's unless a test passes its own: { cookie (the raw pc_ref value), url, storage
 * ({ getItem, setItem, removeItem }), send (body → Promise<boolean>), replaceUrl }.
 * Resolves to whether a click was reported; never rejects.
 */
export async function hydrateReferral(env) {
  try {
    const context = env || browserEnv()
    const cookie = parseReferralCookie(context.cookie)
    if (cookie) {
      storeGroup(context.storage, cookieGroup(cookie))
      storePerKey(context.storage, cookie)
    }
    storeFromLink(context)
    return cookie ? await reportClick(cookie, context) : false
  } catch {
    // Attribution is best-effort — a malformed cookie or a storage-blocked browser
    // (Safari private mode) must never break the page it landed on.
    return false
  }
}
